
import React, { createContext, useContext, useState, useEffect } from "react";
import { config } from "../config";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { apiClient } from "@/utils/apiClient";

type ViewType = "market" | "favorites";

interface Watchlist {
  id: string;
  title: string;
  account_id: string;
  symbols: string | string[];
  created_at: string;
  updated_at: string;
}

interface WatchlistContextType {
  watchlistSymbols: string[];
  activeView: ViewType;
  watchlists: Watchlist[];
  activeWatchlistId: string | null;
  setActiveView: (view: ViewType) => void;
  addToWatchlist: (symbol: string) => Promise<void>;
  addMultipleToWatchlist: (symbols: string[]) => Promise<void>;
  removeFromWatchlist: (symbol: string) => Promise<void>;
  isInWatchlist: (symbol: string) => boolean;
  createWatchlist: (name: string) => Promise<void>;
  renameWatchlist: (id: string, name: string) => Promise<void>;
  deleteWatchlist: (id: string) => Promise<void>;
  setActiveWatchlist: (id: string) => Promise<void>;
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

const generateId = () => Math.random().toString(36).substring(2, 10);

// Modal management utility functions
const enablePointerEvents = () => {
  document.body.style.pointerEvents = 'auto';
};

const disablePointerEvents = () => {
  document.body.style.pointerEvents = 'none';
};

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [watchlistSymbols, setWatchlistSymbols] = useState<string[]>([]);
  const [activeView, setActiveView] = useState<ViewType>("market");
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [activeWatchlistId, setActiveWatchlistId] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  // Load watchlists from API
  useEffect(() => {
    const loadWatchlists = async () => {
      if (!isAuthenticated) return;
      
      try {
        console.log('Loading watchlists from API...');
        const data = await apiClient.get<Watchlist[]>('watchlist');
        console.log('Watchlists loaded:', data);
        
        setWatchlists(data);
        
        // Set the first watchlist as active if we have watchlists
        if (data.length > 0) {
          setActiveWatchlistId(data[0].id);
          
          // Parse symbols from response
          const symbolsArray = parseSymbols(data[0].symbols);
          setWatchlistSymbols(symbolsArray);
        } else {
          setActiveWatchlistId(null);
          setWatchlistSymbols([]);
        }
      } catch (error) {
        console.error("Error loading watchlists:", error);
        toast.error("Failed to load watchlists");
        setWatchlists([]);
        setActiveWatchlistId(null);
        setWatchlistSymbols([]);
      }
    };

    loadWatchlists();
  }, [isAuthenticated, user]);

  // Helper function to parse symbols from API response
  const parseSymbols = (symbols: string | string[]): string[] => {
    if (!symbols) return [];
    if (Array.isArray(symbols)) return symbols;
    try {
      const parsed = JSON.parse(symbols);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Error parsing symbols:", error);
      return [];
    }
  };

  const addToWatchlist = async (symbol: string) => {
    // Check for duplicates
    if (watchlistSymbols.includes(symbol)) {
      return;
    }

    if (!activeWatchlistId) {
      toast.error("No active watchlist selected");
      return;
    }

    const newSymbols = [...watchlistSymbols, symbol];

    // Update local state immediately for live updates
    setWatchlistSymbols(newSymbols);

    // Update active watchlist
    setWatchlists(prev => prev.map(wl => 
      wl.id === activeWatchlistId 
        ? { ...wl, symbols: JSON.stringify(newSymbols) }
        : wl
    ));

    // Make API call to update watchlist symbols
    try {
      console.log('API: Adding symbol to watchlist:', symbol);
      await apiClient.put(`watchlist/${activeWatchlistId}`, { 
        symbols: newSymbols 
      });
      console.log('API: Successfully added symbol to watchlist:', symbol);
      toast.success(`${symbol} added to watchlist`);
    } catch (error) {
      console.error('API: Error adding symbol to watchlist:', error);
      toast.error(`Failed to add ${symbol} to watchlist`);
      
      // Revert local state on error
      setWatchlistSymbols(prev => prev.filter(s => s !== symbol));
      setWatchlists(prev => prev.map(wl => 
        wl.id === activeWatchlistId 
          ? { ...wl, symbols: JSON.stringify(watchlistSymbols) }
          : wl
      ));
    }
  };

  const addMultipleToWatchlist = async (symbols: string[]) => {
    if (!activeWatchlistId) {
      toast.error("No active watchlist selected");
      return;
    }

    // Filter out duplicates and empty symbols
    const uniqueSymbols = symbols.filter(symbol => 
      symbol && !watchlistSymbols.includes(symbol)
    );

    if (uniqueSymbols.length === 0) {
      toast.error("No new symbols to add");
      return;
    }

    const newSymbols = [...watchlistSymbols, ...uniqueSymbols];

    // Update local state immediately for live updates
    setWatchlistSymbols(newSymbols);

    // Update active watchlist
    setWatchlists(prev => prev.map(wl => 
      wl.id === activeWatchlistId 
        ? { ...wl, symbols: JSON.stringify(newSymbols) }
        : wl
    ));

    // Make single API call to update watchlist symbols
    try {
      console.log('API: Adding multiple symbols to watchlist:', uniqueSymbols);
      await apiClient.put(`watchlist/${activeWatchlistId}`, { 
        symbols: newSymbols 
      });
      console.log('API: Successfully added symbols to watchlist:', uniqueSymbols);
      toast.success(`${uniqueSymbols.length} symbol${uniqueSymbols.length > 1 ? 's' : ''} added to watchlist`);
    } catch (error) {
      console.error('API: Error adding symbols to watchlist:', error);
      toast.error(`Failed to add symbols to watchlist`);
      
      // Revert local state on error
      setWatchlistSymbols(watchlistSymbols);
      setWatchlists(prev => prev.map(wl => 
        wl.id === activeWatchlistId 
          ? { ...wl, symbols: JSON.stringify(watchlistSymbols) }
          : wl
      ));
    }
  };

  const removeFromWatchlist = async (symbol: string) => {
    if (!activeWatchlistId) {
      toast.error("No active watchlist selected");
      return;
    }

    const newSymbols = watchlistSymbols.filter(s => s !== symbol);

    // Update local state
    setWatchlistSymbols(newSymbols);

    // Update active watchlist
    setWatchlists(prev => prev.map(wl => 
      wl.id === activeWatchlistId 
        ? { ...wl, symbols: JSON.stringify(newSymbols) }
        : wl
    ));

    // Make API call to update watchlist symbols
    try {
      console.log('API: Removing symbol from watchlist:', symbol);
      await apiClient.put(`watchlist/${activeWatchlistId}`, { 
        symbols: newSymbols 
      });
      console.log('API: Successfully removed symbol from watchlist:', symbol);
      toast.success(`${symbol} removed from watchlist`);
    } catch (error) {
      console.error('API: Error removing symbol from watchlist:', error);
      toast.error(`Failed to remove ${symbol} from watchlist`);
      
      // Revert local state on error
      setWatchlistSymbols(prev => [...prev, symbol]);
      setWatchlists(prev => prev.map(wl => 
        wl.id === activeWatchlistId 
          ? { ...wl, symbols: JSON.stringify([...newSymbols, symbol]) }
          : wl
      ));
    }
  };

  const isInWatchlist = (symbol: string) => {
    return watchlistSymbols.includes(symbol);
  };
  
  const createWatchlist = async (name: string) => {
    if (!name.trim()) {
      toast.error("Watchlist name cannot be empty");
      return;
    }
    
    if (watchlists.some(wl => wl.title.toLowerCase() === name.toLowerCase())) {
      toast.error("A watchlist with this name already exists");
      return;
    }
    
    try {
      console.log('Creating watchlist:', name);
      const newWatchlist = await apiClient.post<Watchlist>('watchlist', { 
        title: name 
      });
      console.log('Watchlist created:', newWatchlist);
      
      setWatchlists(prev => [...prev, newWatchlist]);
      setActiveWatchlistId(newWatchlist.id);
      setWatchlistSymbols([]);
      toast.success(`Watchlist "${name}" created`);
    } catch (error) {
      console.error('Error creating watchlist:', error);
      toast.error('Failed to create watchlist');
    }
  };
  
  const renameWatchlist = async (id: string, name: string) => {
    if (!name.trim()) {
      toast.error("Watchlist name cannot be empty");
      return;
    }
    
    if (watchlists.some(wl => wl.id !== id && wl.title.toLowerCase() === name.toLowerCase())) {
      toast.error("A watchlist with this name already exists");
      return;
    }
    
    const previousTitle = watchlists.find(wl => wl.id === id)?.title;
    
    // Update local state immediately
    setWatchlists(prev => prev.map(wl => 
      wl.id === id ? { ...wl, title: name } : wl
    ));
    
    // Make API call to rename watchlist
    try {
      console.log('API: Renaming watchlist:', id, name);
      await apiClient.put(`watchlist/${id}`, { 
        title: name 
      });
      console.log('API: Successfully renamed watchlist:', id);
      toast.success(`Watchlist renamed to "${name}"`);
    } catch (error) {
      console.error('API: Error renaming watchlist:', error);
      toast.error('Failed to rename watchlist');
      
      // Revert local state on error
      if (previousTitle) {
        setWatchlists(prev => prev.map(wl => 
          wl.id === id ? { ...wl, title: previousTitle } : wl
        ));
      }
    }
  };
  
  const deleteWatchlist = async (id: string) => {
    // Check if this is the default watchlist (id = "default")
    if (id === "default") {
      toast.error("The default watchlist cannot be deleted");
      return;
    }
    
    // Store previous state in case we need to recover
    const previousWatchlists = [...watchlists];
    const previousActiveId = activeWatchlistId;
    
    try {
      // Temporarily disable pointer events during operation
      disablePointerEvents();
      
      // Call the API to delete the watchlist
      await apiClient.delete(`watchlist/${id}`);
      
      setWatchlists(prev => {
        const filtered = prev.filter(wl => wl.id !== id);
        
        // If we're deleting the active watchlist, set the first available one as active
        if (id === activeWatchlistId) {
          if (filtered.length > 0) {
            const newActiveId = filtered[0].id;
            
            // Update the active watchlist and symbols
            setTimeout(() => {
              setActiveWatchlistId(newActiveId);
              setWatchlistSymbols(parseSymbols(filtered[0].symbols));
            }, 0);
          } else {
            // Create a new default watchlist if we deleted all
            const defaultWatchlist: Watchlist = {
              id: generateId(),
              title: "Default",
              account_id: "1",
              symbols: "[]",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            // Important: Return the array with the default watchlist instead of mutating state
            const newWatchlists = [defaultWatchlist];
            
            // Set active watchlist ID and empty symbols
            setTimeout(() => {
              setActiveWatchlistId(defaultWatchlist.id);
              setWatchlistSymbols([]);
            }, 0);
            
            return newWatchlists;
          }
        }
        
        return filtered;
      });
      
      toast.success("Watchlist deleted");
      
      // Force a UI refresh to ensure everything is working
      setTimeout(() => {
        // Re-enable pointer events
        enablePointerEvents();
        
        // This additional timeout ensures React has time to process state changes
        document.body.click(); // Clear any lingering focus or hover states
      }, 150);
      
    } catch (error) {
      console.error("Error deleting watchlist:", error);
      
      // Restore previous state if there was an error
      setWatchlists(previousWatchlists);
      setActiveWatchlistId(previousActiveId);
      
      // Find the symbols for the active watchlist
      if (previousActiveId) {
        const activeList = previousWatchlists.find(wl => wl.id === previousActiveId);
        if (activeList) {
          setWatchlistSymbols(parseSymbols(activeList.symbols));
        }
      }
      
      toast.error("Failed to delete watchlist");
      
      // Make sure pointer events are re-enabled
      enablePointerEvents();
    }
  };
  
  const setActiveWatchlist = async (id: string) => {
    const watchlist = watchlists.find(wl => wl.id === id);
    if (watchlist) {
      setActiveWatchlistId(id);
      
      // Fetch fresh data from API for the specific watchlist
      try {
        console.log('API: Fetching watchlist data:', id);
        const data = await apiClient.get<Watchlist>(`watchlist/${id}`);
        console.log('API: Watchlist data loaded:', data);
        
        // Update local watchlist data
        setWatchlists(prev => prev.map(wl => 
          wl.id === id ? data : wl
        ));
        
        // Set symbols from fresh data
        const symbols = Array.isArray(data.symbols) ? data.symbols : parseSymbols(data.symbols);
        setWatchlistSymbols(symbols);
      } catch (error) {
        console.error('API: Error fetching watchlist data:', error);
        // Fallback to local data
        setWatchlistSymbols(parseSymbols(watchlist.symbols));
        toast.error('Failed to load watchlist data');
      }
    }
  };

  return (
    <WatchlistContext.Provider value={{
      watchlistSymbols,
      activeView,
      watchlists,
      activeWatchlistId,
      setActiveView,
      addToWatchlist,
      addMultipleToWatchlist,
      removeFromWatchlist,
      isInWatchlist,
      createWatchlist,
      renameWatchlist,
      deleteWatchlist,
      setActiveWatchlist
    }}>
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (context === undefined) {
    throw new Error("useWatchlist must be used within a WatchlistProvider");
  }
  return context;
};
